import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarEventModel } from "../../../data/entities";
import { toYearMonthDayLocalString } from "../../../utils";

export type AgendaMode = "upcoming" | "date";

export type AgendaFetcher = (
  startDate: string,
  endDate: string
) => Promise<CalendarEventModel[] | null>;

export const UPCOMING_APPOINTMENT_LIMIT = 20;
const UPCOMING_RANGE_DAYS = 14;
const REFRESH_INTERVAL_MS = 60_000;
const CLOCK_TICK_MS = 30_000;

export function getEventStart(event: CalendarEventModel): Date {
  return new Date(`${event.date.split("T")[0]}T${event.startTime}`);
}

export function getEventEnd(event: CalendarEventModel): Date {
  return new Date(`${event.date.split("T")[0]}T${event.endTime}`);
}

export function useAgendaEvents(
  enabled: boolean,
  fetcher: AgendaFetcher,
  mode: AgendaMode,
  selectedDate: Date
) {
  const [allEvents, setAllEvents] = useState<CalendarEventModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const lastResultJsonRef = useRef("");

  const dateKey = mode === "date" ? toYearMonthDayLocalString(selectedDate) : "";

  const load = useCallback(async (silent: boolean) => {
    if (!enabled) return;
    if (!silent) setIsLoading(true);

    const rangeStart = mode === "date" ? new Date(`${dateKey}T00:00:00`) : new Date();
    const rangeEnd = new Date(rangeStart);
    if (mode === "upcoming") {
      rangeEnd.setDate(rangeEnd.getDate() + UPCOMING_RANGE_DAYS);
    }

    try {
      const result = await fetcher(
        toYearMonthDayLocalString(rangeStart),
        toYearMonthDayLocalString(rangeEnd)
      );
      const eventList = Array.isArray(result) ? result : [];

      const resultJson = JSON.stringify(eventList);
      if (resultJson !== lastResultJsonRef.current) {
        lastResultJsonRef.current = resultJson;
        setAllEvents(eventList);
      }
    } catch {
      // Background refresh failures keep the last known data on screen
      if (!silent) {
        lastResultJsonRef.current = "";
        setAllEvents([]);
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [enabled, fetcher, mode, dateKey]);

  useEffect(() => {
    if (!enabled) {
      lastResultJsonRef.current = "";
      setAllEvents([]);
      setIsLoading(false);
      return;
    }
    lastResultJsonRef.current = "";
    load(false);
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => load(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(interval);
  }, [enabled]);

  const events = useMemo(() => {
    const sorted = allEvents
      .filter((event) => !event.isCancelled)
      .sort((a, b) => getEventStart(a).getTime() - getEventStart(b).getTime());

    if (mode === "date") return sorted;

    return sorted
      .filter((event) => getEventEnd(event) > now)
      .slice(0, UPCOMING_APPOINTMENT_LIMIT);
  }, [allEvents, mode, now]);

  const refresh = useCallback(() => load(true), [load]);

  return { events, isLoading, now, refresh };
}

export default useAgendaEvents;
